import React, { createContext, useContext, useCallback, useMemo } from 'react';
import { useLocalStorage } from '@vastra/ui';
import type { CartItem } from '@vastra/types';
import { useCatalog } from './CatalogContext';
import { useToastContext } from './ToastContext';

interface CartContextValue {
  cart: CartItem[];
  cartCount: number;
  addToCart: (productId: string, quantity: number) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  removeItem: (productId: string) => void;
  clearCart: () => void;
}

const CartContext = createContext<CartContextValue | null>(null);

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}

const CART_KEY = 'vastra_cart';

interface CartProviderProps {
  children: React.ReactNode;
}

export function CartProvider({ children }: CartProviderProps) {
  const { products } = useCatalog();
  const { showToast } = useToastContext();
  const [cart, setCart] = useLocalStorage<CartItem[]>(CART_KEY, []);

  const addToCart = useCallback(
    (productId: string, quantity: number) => {
      const product = products.find((p) => p.id === productId);
      if (!product || product.stock <= 0) return;
      setCart((prev) => {
        const existing = prev.find((item) => item.productId === productId);
        if (existing) {
          const newQty = Math.min(
            existing.quantity + quantity,
            product.stock
          );
          return prev.map((item) =>
            item.productId === productId ? { ...item, quantity: newQty } : item
          );
        }
        return [
          ...prev,
          {
            productId: product.id,
            name: product.name,
            price: product.price,
            quantity: Math.min(quantity, product.stock),
            image: product.images[0]?.url || '',
            sku: product.sku,
            stock: product.stock,
            category: product.category,
          },
        ];
      });
      showToast('Added to bag');
    },
    [products, setCart, showToast]
  );

  const updateQuantity = useCallback(
    (productId: string, quantity: number) => {
      const id = String(productId);
      const qty = Number(quantity);
      if (!Number.isInteger(qty) || qty <= 0) {
        setCart((prev) =>
          prev.filter((item) => String(item.productId) !== id)
        );
        return;
      }
      setCart((prev) =>
        prev.map((item) =>
          String(item.productId) === id ? { ...item, quantity: qty } : item
        )
      );
    },
    [setCart]
  );

  const removeItem = useCallback(
    (productId: string) => {
      setCart((prev) => prev.filter((item) => item.productId !== productId));
      showToast('Removed from bag');
    },
    [setCart, showToast]
  );

  const clearCart = useCallback(() => {
    setCart([]);
  }, [setCart]);

  const cartCount = useMemo(
    () => cart.reduce((sum, item) => sum + item.quantity, 0),
    [cart]
  );

  const value: CartContextValue = {
    cart,
    cartCount,
    addToCart,
    updateQuantity,
    removeItem,
    clearCart,
  };

  return (
    <CartContext.Provider value={value}>{children}</CartContext.Provider>
  );
}
